import { CMSHeader } from '@travelwin/cms/types';

import { useCMSContent } from '../context/CMS';
import { useLocaleUtils } from '../hooks/useLocaleUtils';
import { NavItem } from '../interfaces/NavBar';
import { getPathnameWithLocale } from '../utils/urlUtils';
import { useExternalHomepageConfig } from './useExternalHomepageConfig';

const containsDomain = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const useCmsHeaderItems = (): {
  contactBarItems: NavItem[];
  menuItems: NavItem[];
  brandLogo?: CMSHeader['logo'];
  whatsAppContactButton?: CMSHeader['whatsAppContactButton'];
  showLoginOrMyAccount?: CMSHeader['showLoginOrMyAccount'];
} | null => {
  const { currentLocale } = useLocaleUtils();
  const { getHomepageUrl } = useExternalHomepageConfig();
  const { content } = useCMSContent() || {};

  if (!content) return null;

  const { header } = content;
  if (!header) return null;

  const { headerItems } = header;

  const contactBarItems: NavItem[] = [];
  const menuItems: NavItem[] = [];
  const brandLogo = header.logo;
  const { whatsAppContactButton } = header;
  const { showLoginOrMyAccount } = header;

  const getLinkDestination = (link: string): string => {
    const isExternalLink = containsDomain(link);
    return isExternalLink
      ? getHomepageUrl(new URL(link).pathname)
      : getPathnameWithLocale(link, currentLocale);
  };

  headerItems?.forEach((item) => {
    const navItem: NavItem = {
      id: item.id || '',
      label: item.text,
      visible: true,
      to: item.link ? getLinkDestination(item.link) : undefined,
      items: item.subHeaderItems?.map((subItem) => ({
        id: subItem.id || '',
        label: subItem.text,
        to: getLinkDestination(subItem.link),
        visible: true,
      })),
    };

    if (item.placement === 'contactBar') {
      contactBarItems.push(navItem);
    } else {
      menuItems.push(navItem);
    }
  });

  return {
    contactBarItems,
    menuItems,
    brandLogo,
    whatsAppContactButton,
    showLoginOrMyAccount,
  };
};
